import { useState, useEffect } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";

const EMBLEM = "/rosemoor-emblem.png";

export default function DigitalCard() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/cards/my-card").then((r) => setC(r.data)).catch(() => {}); }, []);
  const downloadCert = async () => {
    try {
      const r = await api.get("/cards/certificate.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rosemoor-certificate-${c.member_number || ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download certificate"); }
  };
  if (!c) return null;
  return (
    <div data-testid="digital-card">
      <div className="rounded-2xl overflow-hidden shadow-lg max-w-md border border-ink/10">
        <div className="bg-maroon h-6" />
        <div className="bg-cream px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-dark-olive font-bold">Rosemoor Community Association</div>
              <div className="font-zilla text-2xl font-semibold text-ink">Membership Card</div>
            </div>
            <div className="w-16 h-16 flex items-center justify-center shrink-0"><img src={EMBLEM} alt="Rosemoor emblem" className="w-16 h-16 object-contain" data-testid="card-emblem" /></div>
          </div>
          <div className="space-y-2.5">
            <Row label="Member" value={c.name} big />
            <div className="grid grid-cols-2 gap-3">
              <Row label="Member No." value={c.member_number} />
              <Row label="Type" value={(c.account_type || "").replace(/_/g, " ")} cap />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Status" value={c.status} cap />
              <Row label="Valid" value={c.valid_to ? `${c.valid_from || "—"} – ${c.valid_to}` : (c.valid_from ? `Since ${c.valid_from}` : "Active member")} />
            </div>
          </div>
        </div>
        <div className="bg-maroon h-6" />
      </div>
      {c.member_number && (
        <div className="max-w-md mt-3 bg-cream border border-ink/15 rounded-xl p-4 flex items-center gap-4" data-testid="card-qr">
          <div className="bg-white p-2 rounded-lg border border-ink/10 shrink-0">
            <QRCodeCanvas value={c.member_number} size={96} data-testid="card-qr-code" />
          </div>
          <div className="text-[13px] text-ink/70">
            <div className="font-semibold text-ink text-[14px] mb-0.5">Check-in code</div>
            Show this to a staff member at the welcome table and they can check you in — no PIN needed.
          </div>
        </div>
      )}
      {c.has_certificate && (
        <button data-testid="download-certificate" onClick={downloadCert}
          className="inline-flex items-center gap-1.5 mt-3 border border-ink/20 rounded-lg px-3 py-2 min-h-[40px] text-[14px] font-semibold">
          <Icon name="Download" className="w-4 h-4" /> Download my {c.certificate_year} membership certificate (PDF)
        </button>
      )}
    </div>
  );
}

function Row({ label, value, big, cap }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-ink/45 font-semibold">{label}</div>
      <div className={`${big ? "text-[18px] font-semibold" : "text-[15px]"} text-ink ${cap ? "capitalize" : ""}`}>{value || "—"}</div>
    </div>
  );
}
